import CardCommon from '@op/components/CardCommon';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import React, { useEffect, useMemo, useState } from 'react';
import './index.scss';
import {
  barIcon,
  lineIcon,
  stackLineIcon,
  SymbolType,
  tableIcon,
  vsIcon,
} from '@op/constants/chart';
import { ratingColors } from '@op/constants';
import { useHistory, useIntl } from 'umi';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import {
  formatTimeXAxisText,
  getChartsGradientColor,
  getXAxisAxisLabel,
} from '@op/utils/chart';
import {
  echartsContent,
  getPercentageInfo,
  getPercentageValue,
  getRationPercentValue,
  sortTimeArrayListFunc,
} from '@op/utils';
import StarBlue from '@/pages/opinion/assets/svg/starblue.svg';
import { Select } from 'tea-component';
import BubbleCommon, {
  mobileGameStoreRating,
} from '@op/components/BubbleCommon';
import {
  decimalFormatPercentage,
  eChartPercentageYAxis,
  reviewsFormat,
} from '@op/utils/value';
import RatingChartTable from '../RatingChartTable';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { GameStoreTwoOptionsType } from '../../constants';
import useGetStoreTimeline from '@op/pages/gamestore/hooks/useGetStoreTimeline';
import { formatDateFunc } from '@op/utils/time';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { goToCompetitorKeyMetrics } from '@op/utils/url';
import DetailWrapper from '@op/pages/Detail/component/DetailWrapper';
import { detailPageTips } from '../../utils';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
const channel: Array<'app store' | 'google play'> = [
  'app store',
  'google play',
];

export const starMapNumber: any = {
  star1: 1,
  star2: 2,
  star3: 3,
  star4: 4,
  star5: 5,
};

type TimelineType = Array<{
  reviews: number;
  score: number;
  star1: number;
  star2: number;
  star3: number;
  star4: number;
  star5: number;
  time: string;
}>;

interface IFormattedRecord {
  time: string;
  name: string;
  excelName: string;
  value: number;
  value_ratio: number;
  total: number;
  excelShare: number;
  excelVal: number;
}

export default function Index({
  filter,
  platform,
  type,
  isDetail = false,
  updateDetailFilter,
  defaultChartType,
  defaultSelected,
  contentCard = true,
  detailPage = false,
  defaultDataType,
}: {
  filter: any;
  platform?: string;
  type: GameStoreTwoOptionsType;
  isDetail?: boolean;
  updateDetailFilter?: (filter: any) => void;
  defaultChartType?: SymbolType;
  defaultSelected?: { [key: string]: boolean };
  contentCard?: boolean;
  detailPage?: boolean;
  defaultDataType?: 'rating' | 'share';
}) {
  const { download, downloading } = useDownloadParams();
  const [chartType, setChartType] = useState<SymbolType>(
    defaultChartType || 'bar',
  );
  const [dataType, setDataType] = useState<'rating' | 'share'>(
    defaultDataType || 'rating',
  );
  const { formatMessage } = useIntl();
  const { globalTrendsTimeline, userScoreTrendsLoading } = useGetStoreTimeline({
    keepParams: filter,
    splitRegion: false,
    channel,
  });
  const subtitle = useMemo(
    () => (type === 'lifetime' ? 'Rating-2' : 'Reviews-4'),
    [type],
  );

  useEffect(() => {
    if (defaultDataType) {
      return;
    }
    if (type === 'lifetime') {
      setDataType('share');
    } else {
      setDataType('rating');
    }
  }, [type, defaultDataType]);

  const { timeline, xAxis } = useMemo(() => {
    const list = globalTrendsTimeline[platform] || [];
    const formatDate = formatDateFunc(filter.date_type);
    const keyMap: {
      [key: string]: {
        lifetime: string | Array<string> | { key: string };
        selected: string | Array<string> | { key: string };
      };
    } = {
      reviews: {
        lifetime: 'lifetime_reviews',
        selected: 'selected_reviews',
      },
      score: {
        lifetime: 'lifetime_score',
        selected: 'selected_score',
      },
      star: {
        lifetime: { key: 'lifetime' },
        selected: ['star1', 'star2', 'star3', 'star4', 'star5'],
      },
    };
    const xAxis = new Set();
    // list.sort(sortTimeArrayListFunc());
    const data: TimelineType = list.map((item: any) => {
      const { time } = item;
      const itemInfo = Object.keys(keyMap).reduce(
        (pre, valueKey) => {
          const obj = keyMap[valueKey];
          const keys = obj[type];
          if (typeof keys === 'string') {
            return { ...pre, [valueKey]: item[keys] };
          }

          if (Array.isArray(keys)) {
            return {
              ...pre,
              ...keys.reduce(
                (preObj, key) => ({ ...preObj, [key]: item[key] }),
                {},
              ),
            };
          }

          const { key } = keys;

          return { ...pre, ...item[key] };
        },
        { time },
      );

      xAxis.add(formatDate(time));
      return itemInfo;
    });
    const xTimes: Array<string> = Array.from(xAxis) as any;
    xTimes.sort(sortTimeArrayListFunc('asc'));
    if (dataType === 'rating') return { timeline: data, xAxis: xTimes };

    // 计算占比的情况
    const percentData = data.map((item: any) => {
      const reviews = item.reviews || 0;
      return {
        ...item,
        ...[1, 2, 3, 4, 5].reduce((pre, key) => {
          const newKey = `star${key}`;
          const value = getPercentageValue(item?.[newKey] || 0, reviews, 4);
          return { ...pre, [newKey]: value, [`${newKey}_ori`]: item?.[newKey] };
        }, {}),
      };
    });
    return { timeline: percentData, xAxis: xTimes };
  }, [dataType, filter, globalTrendsTimeline, platform, type]);

  const selectOption = useMemo(
    () => [
      {
        value: 'share',
        text: formatMessage({ id: 'By Rating%' }),
        button: 'By Rating%',
      },
      {
        value: 'rating',
        text: (
          <div className="dbo-rating-chart-select">
            <BubbleCommon
              tipsMap={
                platform === 'google play' && type === 'lifetime'
                  ? mobileGameStoreRating
                  : null
              }
              showIcon={false}
              placement="right"
            >
              {formatMessage({ id: 'By Rating' })}
              {platform === 'google play' && type === 'lifetime' && (
                <span className="select-option">
                  {' '}
                  ({formatMessage({ id: 'Not Recommended' })})
                </span>
              )}
            </BubbleCommon>
          </div>
        ),
        button: 'By Rating',
      },
    ],
    [type, platform, formatMessage],
  );

  const empty = useMemo(() => {
    let score = 0;
    timeline.forEach((val) => (score += val?.reviews || 0));
    return score === 0;
  }, [timeline]);

  const history = useHistory();
  function changeChartType(type: any) {
    if (type === 'vs') {
      goToCompetitorKeyMetrics(history, filter, 'Game Store');
    } else {
      setChartType(type || 'bar');
    }
  }
  const records = useMemo<IFormattedRecord[]>(() => {
    const result: IFormattedRecord[] = [];
    const previousValueArr: { [key: string]: number } = {};
    timeline
      ?.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
      ?.forEach((val) => {
        const stars = ['star5', 'star4', 'star3', 'star2', 'star1'];
        stars.forEach((star) => {
          const value = val[star];
          let previousValue = previousValueArr[star] || 0;
          previousValueArr[star] = value;

          const value_ratio = getRationPercentValue(value, previousValue, 6);
          previousValue = value;
          const originalValue = val?.[`${star}_ori`] || value;
          result.push({
            name: star,
            excelName: star.replace('star', ''),
            time: val.time,
            value,
            value_ratio,
            total: val.reviews,
            excelShare: originalValue / val.reviews,
            excelVal: originalValue,
          });
        });
      });
    // console.log(' resullttt ', result, timeline);
    return result.sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    );
  }, [timeline]);

  const { Competitor_Rank_Hide } = useGetSingePageSourceRule({
    keys: ['Competitor_Rank_Hide'],
  });
  function getOperationsList() {
    const list = [tableIcon, lineIcon, stackLineIcon, barIcon];

    if (!Competitor_Rank_Hide) {
      list.unshift(vsIcon);
    }

    return list;
  }

  return (
    <CardCommon
      className="dbo-rating-chart"
      contentCard={contentCard}
      operations={getOperationsList()}
      operationInfo={[chartType, changeChartType]}
      hasDownload
      downloadLoading={downloading}
      loadingHeight={218}
      emptyHeight={218}
      title={
        <span className="dbo-rating-chart-select-wrapper">
          <Select
            appearance="default"
            options={selectOption}
            value={dataType}
            onChange={(value: any) => {
              buriedBtnPoint('KMGS_ByMetricsType');
              setDataType(value);
            }}
            overlayClassName="dbo-select-footer"
            className="dbo-select-hover"
            listWidth={212}
            button={
              <BubbleCommon
                placement="top"
                tipsMap={
                  platform === 'google play' &&
                  type === 'lifetime' &&
                  dataType === 'rating'
                    ? mobileGameStoreRating
                    : null
                }
                showIcon={
                  platform === 'google play' &&
                  type === 'lifetime' &&
                  dataType === 'rating'
                }
              >
                <div className="dbo-select-text">
                  {formatMessage({
                    id: selectOption.filter((val) => val.value === dataType)[0]
                      .button,
                  })}
                </div>
              </BubbleCommon>
            }
          />
        </span>
      }
      handleDownloadOnClick={() => {
        download({
          key: 'KeyMetricsGameStoreRating',
          fileName: 'Game Store Rating',
          filter,
          auxiliaryParams: {},
          data: records,
        });
      }}
      empty={empty}
      loading={userScoreTrendsLoading}
      logBtnId="KMGS"
      extraInfo={{ platform, type }}
    >
      <DetailWrapper
        eventProps={{
          key: 'KeyMetricsMobileRatingChart',
          defaultFilter: {
            ...filter,
            splitRegion: false,
            channel: [platform],
          },
          defaultProps: {
            platform,
            type,
            dataType,
            defaultDataType: dataType,
            defaultChartType: chartType,
            contentCard: false,
            detailPage,
          },
          paramsKey: {
            bar: {
              start_time: 'name',
              end_time: 'name',
              reviews_rating: 'seriesName',
            },
            line: {
              start_time: 'name',
              end_time: 'name',
              reviews_rating: 'seriesName',
            },
            stack_line: {
              start_time: 'name',
              end_time: 'name',
              reviews_rating: 'seriesName',
            },
            pie: {
              reviews_rating: 'name',
            },
            table: {
              reviews_rating: 'name',
            },
          },
          chartType,
        }}
        isDetail={isDetail}
        updateDetailFilter={updateDetailFilter}
        formatParamsKey={(value, key) => {
          if (key === 'reviews_rating') {
            return value?.replace('star', '');
          }
          return value;
        }}
      >
        {({ eventsDict }) => (
          <>
            {chartType === 'table' ? (
              <RatingChartTable
                records={records}
                filter={filter}
                dataType={dataType}
                subtitle={subtitle}
                eventsDict={eventsDict}
                className={detailPage ? 'dbo-tablecommon-hide-checkbox' : ''}
              />
            ) : (
              <RatingChart
                filter={filter}
                chartType={chartType}
                dataType={dataType}
                timeline={timeline}
                xAxis={xAxis}
                subtitle={subtitle}
                eventsDict={eventsDict}
                defaultSelected={defaultSelected}
                detailPage={detailPage}
              />
            )}
          </>
        )}
      </DetailWrapper>
    </CardCommon>
  );
}

function RatingChart({
  filter,
  chartType,
  dataType,
  timeline = [],
  xAxis = [],
  subtitle,
  eventsDict,
  defaultSelected,
  detailPage,
}: {
  filter: any;
  chartType: SymbolType;
  dataType: 'rating' | 'share';
  timeline?: TimelineType;
  xAxis?: Array<string>;
  subtitle: string;
  eventsDict?: any;
  defaultSelected?: { [key: string]: boolean };
  detailPage: boolean;
}) {
  const { formatMessage } = useIntl();
  const realChartType = chartType === 'stack_line' ? 'line' : chartType;

  const tooltip = `${dataType === 'share' ? `${subtitle}%` : subtitle}`;

  const initMentions = () => {
    const legends: Array<any> = [];
    const totalList: Array<number> = [];
    const series: any = [1, 2, 3, 4, 5].map((cur, index) => {
      const color = ratingColors[index];
      const name = `star${cur}`;
      const obj: any = {
        name: `star${cur}`,
        type: realChartType,
        itemStyle: { color },
        data: [],
      };
      if (chartType !== 'line') {
        obj.stack = 'Total';
      }
      if (chartType === 'stack_line') {
        obj.areaStyle = getChartsGradientColor(color, 0, 0.5);
      }
      legends.push({ name, itemStyle: { color } });
      return obj;
    });

    timeline.forEach((valueItem: any, index: number) => {
      totalList[index] = 0;
      series.forEach((item: any) => {
        const key = item.name;
        const value = valueItem?.[key] || 0;
        if (item?.data) {
          totalList[index] += value;
          item.data.push(value);
        }
      });
    });

    if (chartType === 'bar' && dataType !== 'share') {
      series.push({
        name: 'Total',
        type: 'bar',
        stack: 'Total',
        data: totalList.map(() => 0),
        label: {
          normal: {
            show: totalList?.length < 20,
            position: 'top', //  位置设为top
            formatter: (c: any) => {
              return echartsContent(totalList[c.dataIndex]) || '';
            },
            textStyle: { color: '#444' },
          },
        },
        itemStyle: { color: 'rgba(0,0,0,0)' },
      });
    }

    return {
      tooltip: {
        formatter: (param: any) => {
          let currentDateTotal = 0;
          const params = param.filter((val: any) => val.seriesName !== 'Total');
          params?.forEach(
            (item: { value: number }) => (currentDateTotal += item?.value),
          );
          const sumText =
            subtitle === 'Reviews-4'
              ? formatMessage({ id: 'Sum of Reviews-1' })
              : formatMessage({ id: 'Sum of Rating' });

          return formatChartParamsDate({
            title: listTimeTitleFunc(params[0]?.name, filter.date_type),
            params,
            total: currentDateTotal,
            isSort: true,
            valueFormat: (value: number) => {
              if (dataType === 'share') {
                return decimalFormatPercentage(value);
              }
              return reviewsFormat(value);
            },
            dealwithRecords(records, total) {
              if (dataType === 'share') {
                return;
              }
              const newRecords: Array<any> = records.map((item) => {
                const share = getPercentageInfo(item.originValue, total);
                return {
                  ...item,
                  value: `${item.value}${
                    !item.originValue ? '' : ` (${share})`
                  }`,
                };
              });

              newRecords.unshift({
                text: sumText,
                value: reviewsFormat(total),
                originValue: total,
                originText: '',
              });
              const finalRecords = detailPageTips(
                newRecords,
                formatMessage,
                detailPage,
              );
              return finalRecords.filter((item) => Boolean(item));
            },
            textShowFun: (marker, seriesName) => {
              let icon = '';
              const value = starMapNumber[seriesName];
              for (let i = 0; i < value; i++) {
                icon += `<img src='${StarBlue}'/>`;
              }
              return `<span class="chart-common-formatter-marker">${marker}</span>&nbsp;${icon}`;
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: formatMessage({ id: 'Rating-1' }),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: tooltip }),
                },
              ];

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
      },
      legend: {
        data: legends.reverse(),
        formatter: (value: any) => {
          let string = '';
          const index = starMapNumber[value];
          for (let i = 0; i < index; i++) {
            string += `{star|}`;
          }
          return string;
        },
        textStyle: {
          rich: {
            star: {
              align: 'left',
              overflow: 'none',
              width: 10,
              height: 10,
              backgroundColor: {
                image: StarBlue,
              },
            },
          },
        },
        ...(defaultSelected ? { selected: defaultSelected } : {}),
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter?.date_type),
      ),
      xAxis: [
        {
          type: 'category',
          data: xAxis,
          axisLabel: getXAxisAxisLabel(filter),
        },
      ],
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              if (dataType === 'rating') {
                return echartsContent(value);
              }

              return decimalFormatPercentage(value, '0', 0);
            },
          },
          ...(dataType === 'share' ? { max: 1, min: 0 } : {}),
        },
      ],
      series,
      grid: {
        left: 0,
        top: 20,
      },
    };
  };

  const options = useMemo(() => {
    return initMentions();
  }, [timeline, chartType, dataType, formatMessage, defaultSelected]);

  return (
    <EChartCommon
      options={options}
      leftTitle={formatMessage({ id: tooltip })}
      optionsProps={{ type: realChartType }}
      height={180}
      eventsDict={eventsDict}
    />
  );
}
